import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  CreatedUser,
  CreateUserRequest,
  CurrentUser,
  CustomerRole,
  FoundUsersPage,
  GrantMembershipRequest,
  OrganisationId,
  ProvisionableOrganisations,
  SessionContextChoice,
  UpdateUserProfileRequest,
  User,
  UserDirectoryPage,
  UserId,
  UserMemberships,
} from "@thalamos/haven-common";
import {
  CreatedUserSchema,
  CurrentUserSchema,
  FoundUsersPageSchema,
  ProvisionableOrganisationsSchema,
  toSetSessionContextRequest,
  UserDirectoryPageSchema,
  UserMembershipsSchema,
  UserSchema,
} from "@thalamos/haven-common";

import { config as appConfig } from "../../config/index.js";
import type { DirectoryQuery } from "../admin/api.js";
import { directoryParams } from "../admin/api.js";
import type { ErrorBody, NoContentBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseResponseCurrentUser = parseAxiosResponse(CurrentUserSchema);
const parseResponseUser = parseAxiosResponse(UserSchema);
const parseResponseMemberships = parseAxiosResponse(UserMembershipsSchema);
const parseResponseFoundUsers = parseAxiosResponse(FoundUsersPageSchema);
const parseResponseUserDirectory = parseAxiosResponse(UserDirectoryPageSchema);
const parseResponseProvisionable = parseAxiosResponse(
  ProvisionableOrganisationsSchema,
);

/**
 * A created account answers 201, which `parseAxiosResponse` leaves alone, so
 * the body is parsed here when it arrives. A refusal keeps its error body.
 */
const parseCreated = (response: {
  status: number;
  data: unknown;
}): ResponseBody<CreatedUser, ErrorBody> =>
  response.status === 201
    ? CreatedUserSchema.parse(response.data)
    : (response.data as ErrorBody);

export const config = {
  user: {
    /**
     * `sessionContextOrganisationId` and `sessionContextKind` ask the server to
     * resolve the session against a specific organisation and kind of work,
     * which is how an emailed link lands the recipient in the context the link
     * was issued for.
     */
    me: (
      sessionContextOrganisationId?: string,
      sessionContextKind?: string,
    ) => ({
      url: "/api/users/me",
      method: "get",
      ...(sessionContextOrganisationId
        ? {
            params: {
              "session-context-organisation-id": sessionContextOrganisationId,
              ...(sessionContextKind
                ? { "session-context-kind": sessionContextKind }
                : {}),
            },
          }
        : {}),
      transformResponse: parseResponseCurrentUser,
    }),
    setSessionContext: (context: SessionContextChoice) => ({
      url: "/api/users/context",
      method: "post",
      data: toSetSessionContextRequest(context),
    }),
    /**
     * Deliberately the one authenticated call that does not extend the session,
     * so polling it detects an idle timeout rather than preventing one.
     */
    signedInCheck: () => ({ url: "/api/users/signed-in-check", method: "get" }),
    /**
     * People who could be named to a young person's record, matched by name.
     *
     * A term is always sent and the server refuses a short one — finding
     * never lists, because the workforce of every customer would otherwise be
     * pageable by anybody running a case.
     */
    find: (query: string, limit?: number) => ({
      url: "/api/users",
      method: "get",
      params: { query, ...(limit === undefined ? {} : { limit }) },
      transformResponse: parseResponseFoundUsers,
    }),
    /**
     * The accounts the session administers, as a page of the directory. The
     * server decides which those are; an absent `query` lists all of them.
     */
    directory: (request: DirectoryQuery = {}) => ({
      url: "/api/users/directory",
      method: "get",
      params: directoryParams(request),
      transformResponse: parseResponseUserDirectory,
    }),
    /** The organisations the session may give somebody a membership at. */
    provisionableOrganisations: () => ({
      url: "/api/users/directory/organisations",
      method: "get",
      transformResponse: parseResponseProvisionable,
    }),
    create: (body: CreateUserRequest) => ({
      url: "/api/users",
      method: "post",
      data: body,
    }),
    /** Answers 204; 400 for an account that signs in through its organisation. */
    sendPasswordReset: (userId: UserId) => ({
      url: `/api/users/${userId}/password-reset`,
      method: "post",
    }),
    /** Somebody else's account: who they are, not what they may do. */
    byId: (userId: string) => ({
      url: `/api/users/${userId}`,
      method: "get",
      transformResponse: parseResponseUser,
    }),
    /**
     * Changes what an account says about the person behind it, and answers with
     * the account as it now stands.
     *
     * The same call whether the account is the caller's own or one they
     * administer: which of the two it is decides nothing here, and the server
     * refuses whichever it may not.
     *
     * Parsed like the read, so the declared response type is one a caller may
     * act on. `parseAxiosResponse` validates only a 200, which is what leaves a
     * refusal's body to reach the caller as the error shape it is.
     */
    updateProfile: (userId: UserId, body: UpdateUserProfileRequest) => ({
      url: `/api/users/${userId}`,
      method: "patch",
      data: body,
      transformResponse: parseResponseUser,
    }),
    /** What an account may act as — a separate disclosure from who they are. */
    memberships: (userId: string) => ({
      url: `/api/users/${userId}/memberships`,
      method: "get",
      transformResponse: parseResponseMemberships,
    }),
    /**
     * Grants a membership, and answers with the set the account then holds.
     *
     * The three membership calls all answer with the whole set, so a caller
     * renders what the server has rather than what it hoped for — and the list
     * cannot drift from it by patching one entry at a time.
     */
    grantMembership: (userId: UserId, body: GrantMembershipRequest) => ({
      url: `/api/users/${userId}/memberships/grant`,
      method: "post",
      data: body,
      transformResponse: parseResponseMemberships,
    }),
    /**
     * Takes a membership away. Both the organisation and the role are in the
     * path because both identify it: somebody may hold two roles at one
     * organisation.
     */
    revokeMembership: (
      userId: UserId,
      organisationId: OrganisationId,
      role: CustomerRole,
    ) => ({
      url: `/api/users/${userId}/memberships/${organisationId}/${role}`,
      method: "delete",
      transformResponse: parseResponseMemberships,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    user: {
      me: (
        sessionContextOrganisationId?: string,
        sessionContextKind?: string,
      ) =>
        apiAxios.request<CurrentUser>(
          config.user.me(sessionContextOrganisationId, sessionContextKind),
        ),
      setSessionContext: (context: SessionContextChoice) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.user.setSessionContext(context),
        ),
      signedInCheck: () => apiAxios.request(config.user.signedInCheck()),
      find: (query: string, limit?: number) =>
        apiAxios.request<ResponseBody<FoundUsersPage, ErrorBody>>(
          config.user.find(query, limit),
        ),
      create: async (body: CreateUserRequest) => {
        const response = await apiAxios.request<unknown>(
          config.user.create(body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      sendPasswordReset: (userId: UserId) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.user.sendPasswordReset(userId),
        ),
      byId: (userId: string) =>
        apiAxios.request<ResponseBody<User, ErrorBody>>(
          config.user.byId(userId),
        ),
      updateProfile: (userId: UserId, body: UpdateUserProfileRequest) =>
        apiAxios.request<ResponseBody<User, ErrorBody>>(
          config.user.updateProfile(userId, body),
        ),
      memberships: (userId: string) =>
        apiAxios.request<ResponseBody<UserMemberships, ErrorBody>>(
          config.user.memberships(userId),
        ),
      grantMembership: (userId: UserId, body: GrantMembershipRequest) =>
        apiAxios.request<ResponseBody<UserMemberships, ErrorBody>>(
          config.user.grantMembership(userId, body),
        ),
      revokeMembership: (
        userId: UserId,
        organisationId: OrganisationId,
        role: CustomerRole,
      ) =>
        apiAxios.request<ResponseBody<UserMemberships, ErrorBody>>(
          config.user.revokeMembership(userId, organisationId, role),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    user: {
      me: function useUserMe(
        sessionContextOrganisationId?: string,
        sessionContextKind?: string,
      ) {
        return useApiAxios<CurrentUser>(
          config.user.me(sessionContextOrganisationId, sessionContextKind),
        );
      },
      byId: function useUserById(userId: string) {
        return useApiAxios<User>(config.user.byId(userId));
      },
      find: function useUserFind(query: string, limit?: number) {
        return useApiAxios<FoundUsersPage>(config.user.find(query, limit));
      },
      memberships: function useUserMemberships(userId: string) {
        return useApiAxios<UserMemberships>(config.user.memberships(userId));
      },
      directory: function useUserDirectory(
        request: DirectoryQuery = {},
        options?: Parameters<typeof useApiAxios<UserDirectoryPage>>[1],
      ) {
        return useApiAxios<UserDirectoryPage>(
          config.user.directory(request),
          options,
        );
      },
      provisionableOrganisations: function useProvisionableOrganisations(
        options?: Parameters<typeof useApiAxios<ProvisionableOrganisations>>[1],
      ) {
        return useApiAxios<ProvisionableOrganisations>(
          config.user.provisionableOrganisations(),
          options,
        );
      },
    },
  };
};

/**
 * Ending the Auth0 session as well as ours matters: clearing only the server
 * session would leave the identity provider willing to sign the same user
 * straight back in. Auth0 returns to the API's logout route, which destroys the
 * server session and then redirects to this app's sign-in page.
 */
export const signOutUrl = (): string => {
  const url = new URL("/v2/logout", `https://${appConfig.auth0.domain}`);
  url.searchParams.set("client_id", appConfig.auth0.clientId);
  url.searchParams.set(
    "returnTo",
    new URL("/api/oauth/logout", appConfig.backendUrl).toString(),
  );
  return url.toString();
};

/**
 * The server's sign-out route alone, for a guest: they signed in through their
 * link, so there is no identity-provider session to end.
 */
export const guestSignOutUrl = (): string =>
  new URL("/api/oauth/logout", appConfig.backendUrl).toString();

/**
 * Signing in is entirely server-driven: the browser navigates to the API, which
 * performs the OAuth exchange and redirects back here. `frontend` tells the
 * server which of its configured frontend origins to return to — it is
 * validated against an allowlist server-side, so an unrecognised value falls
 * back to the server's default rather than redirecting anywhere it is told.
 */
export const startSignInUrl = (returnTo?: string): string => {
  const url = new URL("/api/oauth/login", appConfig.backendUrl);
  url.searchParams.set("frontend", window.location.origin);
  if (returnTo) {
    url.searchParams.set("returnTo", returnTo);
  }
  return url.toString();
};
